import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import DruidWebchatContext, { DruidWebchatContextType, WebChatState } from "../WebChatContext";
import { useMergedOptions } from '../selectors/useMergedOptions';
import classNames from "classnames";
import DruidActivityStatus from '../components/DruidActivityStatus';
import { unstable_batchedUpdates } from "react-dom";
import ImageAttachmentWrapper from "../components/ImageAttachmentWrapper";
import HeroCardWrapper from "../components/HeroCardWrapper";
import ThumbnailCardWrapper from "../components/ThumbnailCardWrapper";
import { DruidImageViewHandle } from "../components/DruidImagesViewer";
import CarouselLayout from 'botframework-webchat-component/lib/Activity/CarouselLayout';
import StackedLayout from 'botframework-webchat-component/lib/Activity/StackedLayout';
import isUrl from 'validator/es/lib/isURL';
import { WebChatOptions } from "../WebChatOptions";
import { singleToArray, OneOrMany, DirectLineActivity } from "botframework-webchat-core";
import CardActionMiddleware from "botframework-webchat-api/lib/types/CardActionMiddleware";
import AttachmentMiddleware from "botframework-webchat-api/lib/types/AttachmentMiddleware";
import ActivityMiddleware from "botframework-webchat-api/lib/types/ActivityMiddleware";
import { ActivityStatusMiddleware } from "botframework-webchat-api/lib/types/ActivityStatusMiddleware";
import ScrollToEndButtonMiddleware from "botframework-webchat-api/lib/types/ScrollToEndButtonMiddleware";
import AdaptiveCardContent from 'botframework-webchat/lib/adaptiveCards/Attachment/AdaptiveCardContent';
import activityFromBot from 'botframework-webchat-core/src/definitions/activityFromBot';
import { FaRegThumbsUp, FaRegThumbsDown, FaThumbsUp, FaThumbsDown } from "react-icons/fa";

import { BehaviorSubject } from 'rxjs';
import { removeTapFromImagesAttachmentIfEmpty, shouldEnter_fileUploadInputMode, shouldEnter_sensitiveDataInputMode } from "../logics/druidBusinessLogic";
import useDruidLocalize from "./useDruidLocalize";
import DruidActivityCustomTimestamp from "../components/DruidActivityCustomTimestamp";
import { ignorableActivity } from "../logics/ignorableActivity";
import { findNestedObjectByKey } from "../utils/arrayUtils";
import { ConversationHistorySeparator } from "../components/ConversationHistorySeparator";
import DruidActivityWrapper from "../components/DruidActivityWrapper";
import { hooks } from 'botframework-webchat-component';
import useStyleToEmotionObject from 'botframework-webchat-component/lib/hooks/internal/useStyleToEmotionObject';

import CroppedImage from "botframework-webchat-component/lib/Utils/CroppedImage"
import AvatarMiddleware from "botframework-webchat-api/lib/types/AvatarMiddleware";


const { useStyleSet, useDirection, useLocalizer } = hooks;

export const useActivityStatusMiddleware = (projectedMergedChatOptions: WebChatOptions) => {
  const [storeState, dispatch, , druidStyleToEmotionObject] = useContext<DruidWebchatContextType>(DruidWebchatContext);

  const { UI: { activityStatusShowBotName, activityStatusBotName, activityStatusUserName, activityStatusShowUserName, activityStatusReadColor, customTimestampFormat, botActivitiesActionsEnabled } = {},
    styleOptions: {  timestampColor, fontSizeSmall, subtle, sendBoxButtonColorOnHover, accent } = {}
  } = useMergedOptions();
  return [...singleToArray(projectedMergedChatOptions.activityStatusMiddleware), () => next => args => {
    const ROOT_STYLE = {
      '&.druid__webchat__activityStatus__timestamp': {
        display: 'flex',
        '& .druid__webchat__activityStatus__timestampPretext': {
          '& > span': {
            fontSize: fontSizeSmall,
            color: timestampColor || subtle
          }

        }
      },
      'span.activityStatus__sendStatus': {
        fontSize: fontSizeSmall,
        color: timestampColor || subtle
      },
      'button.druid__webchat__bot-activity-actions-button': {
        background: 'none',
        border: 'none',
        padding: '0px 5px',
        'svg': {
          width: '14px',
          height: '14px',
          fill: subtle,
        },
        '&:not([aria-disabled="true"]) svg:hover': {
          fill: accent
        }
      }
    };

    const ACTIVITY_STATUS_STYLE = {
      '&.activityStatus__sendStatus': {
        fontSize: fontSizeSmall,
        color: timestampColor || subtle
      }
    }
    const rootClassName = druidStyleToEmotionObject(ROOT_STYLE) + '';
    const activityStatusClassName = druidStyleToEmotionObject(ACTIVITY_STATUS_STYLE) + '';
    const {
      activity,
      activity: {
        id,
        from: { role, name },
        status,
        attachments = [],
        channelData: { attachmentSizes: [firstAttachmentSize] = [] } = {}
      },
      sendState,
      sameTimestampGroup
    } = args;
    const botOrUserName = role === 'user' ? activityStatusShowUserName && (activityStatusUserName || name) : activityStatusShowBotName && (activityStatusBotName || name);
    if (sendState === 'sending') {
      return next(args) || <span className={classNames('activityStatus__sendStatus', activityStatusClassName + '')}>Sending&hellip;</span>;
    } else if (sendState === 'send failed') {
      const [firstAttachment] = attachments;
      if(firstAttachment && firstAttachment.contentType && !firstAttachment.thumbnailUrl && !firstAttachment.contentUrl && firstAttachmentSize) { 
        // large size uploads to DL are rejected. when retrying, contentUrl is removed from the activity - patchActivity (botframework-webchat-core\src\reducers\createActivitiesReducer.ts)
        return <span className={classNames('activityStatus__sendStatus activityStatus__sendStatus_failed', activityStatusClassName + '')}> { firstAttachmentSize > 4194304 ? 'File is too big' : 'Failed to send'}</span>
      }
      return  next(args) || <span className={classNames('activityStatus__sendStatus activityStatus__sendStatus_failed', activityStatusClassName + '')}>Failed to send</span>;
    } else if (!sameTimestampGroup) {
      return (
        <span className={classNames('druid__webchat__activityStatus__timestamp', rootClassName + '')} >
          {<span className={classNames('druid__webchat__activityStatus__timestampPretext', botOrUserName === 'anonymous' && 'druid__webchat__activityStatus__is-anonymous')}><span>{botOrUserName}{botOrUserName && <>&nbsp;-&nbsp;</>}</span></span>}
          <span className="druid__webchat__activityStatus__timestampContent">{
            customTimestampFormat ?
              <DruidActivityCustomTimestamp activity={activity}></DruidActivityCustomTimestamp>
              :
              <>{next(args)}</>
          }</span>
          {role !== 'bot' && <DruidActivityStatus status={status} activityStatusReadColor={activityStatusReadColor} className="druid__webchat__activityStatus__deliveryStatus"></DruidActivityStatus>}
          {botActivitiesActionsEnabled && role === 'bot' && !!activity.text && 
              <>
                <CopyButton text={activity.text} />
                <ReactionButtons storeState={storeState} activity={activity} activities={storeState?.store?.getState()?.activities} key={storeState?.store?.getState()?.activities?.length}></ReactionButtons>
              </>
          }
        </span>
      );
    }
    return next(args);
  }].filter((item) => !!item) as OneOrMany<ActivityStatusMiddleware>;
}

const CopyButton = React.memo(({ text }: { text: string }) => {
  const [copySucceeded, setCopySucceeded] = useState(false);
  
  const copyMessageToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySucceeded(true);
      setTimeout(() => {
        setCopySucceeded(false);
      }, 1000);
    } catch (err) {
      setCopySucceeded(false);
    }
  };

  return (
    <button className="copyToClipboard druid__webchat__bot-activity-actions-button" onClick={copyMessageToClipboard} title={copySucceeded ? 'Copied' : 'Copy message'}>
      {copySucceeded ? 
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M400 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V80c0-26.51-21.49-48-48-48zm0 400H48V80h352v352zm-35.864-241.724L191.547 361.48c-4.705 4.667-12.303 4.637-16.97-.068l-90.781-91.516c-4.667-4.705-4.637-12.303.069-16.971l22.719-22.536c4.705-4.667 12.303-4.637 16.97.069l59.792 60.277 141.352-140.216c4.705-4.667 12.303-4.637 16.97.068l22.536 22.718c4.667 4.706 4.637 12.304-.068 16.971z"/></svg>
        :
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M464 0H144c-26.51 0-48 21.49-48 48v48H48c-26.51 0-48 21.49-48 48v320c0 26.51 21.49 48 48 48h320c26.51 0 48-21.49 48-48v-48h48c26.51 0 48-21.49 48-48V48c0-26.51-21.49-48-48-48zM362 464H54a6 6 0 0 1-6-6V150a6 6 0 0 1 6-6h42v224c0 26.51 21.49 48 48 48h224v42a6 6 0 0 1-6 6zm96-96H150a6 6 0 0 1-6-6V54a6 6 0 0 1 6-6h308a6 6 0 0 1 6 6v308a6 6 0 0 1-6 6z"/></svg>
      }
    </button>
  );
});

const ReactionButtons = React.memo(({ existingLike, existingDislike, storeState, activity, activities }: { existingLike?: boolean, existingDislike?: boolean, storeState: WebChatState, activity?: any, activities?: any[] }) => {
  const [likeFeedbackSent, setLikeFeedbackSent] = useState(!!existingLike);
  const [dislikeFeedbackSent, setDislikeFeedbackSent] = useState(!!existingDislike);
  const sendFeedback = useCallback(async (isLike: boolean) => {
    await storeState.directlineInstance?.postActivity({
      from: { id: storeState.mergedChatOptions.userId || "" },
      // name: isLike ? "PositiveFeedbackEvent" : "NegativeFeedbackEvent",
      name: isLike ? "_positive_feedback_" : "_negative_feedback_",
      channelData: { feedbackActivityId: activity.id, sourceMessageId: activity.channelData?.sourceMessageId },
      type: 'event',
      value: { $entityTypeName$: 'ChatActivityData',
         FeedbackFlows: isLike? '5' : '1', FeedbackBotMessage: activity.text, FeedbackActivityId: activity.id,
         FeedbackMessage: null, FeedbackReaction: isLike ? "like": "dislike", IsCommandChannel: !!storeState.mergedChatOptions?._isSecondaryChannel
        }
    }).toPromise();
    if(isLike) {
      setLikeFeedbackSent(true)
    } else {
      setDislikeFeedbackSent(true)
    }
  }, [storeState, activity]);

  // const isFeedbackSend = !!(activities?.find(act => act.channelData?.feedbackActivityId === activity.id))

  return (
    <>
      {!dislikeFeedbackSent && 
        <button className="druid__webchat__bot-activity-actions-button" aria-disabled={likeFeedbackSent} onClick={() => !likeFeedbackSent && sendFeedback(true)} title={'Like'}>
          {/* {(likeFeedbackSent ? <FaThumbsUp/>: <FaRegThumbsUp/>)} */}
          {(likeFeedbackSent ? <FaThumbsUp/>: <FaRegThumbsUp/>)}

        </button>
      }
      {!likeFeedbackSent && 
        <button className="druid__webchat__bot-activity-actions-button" aria-disabled={dislikeFeedbackSent} onClick={() => !dislikeFeedbackSent && sendFeedback(false)} title={'Dislike'}>
          {!likeFeedbackSent && (dislikeFeedbackSent ? <FaThumbsDown/>: <FaRegThumbsDown/>)}
        </button>
      }
    </>
  );
});

export const useCardActionMiddleware = (projectedMergedChatOptions: WebChatOptions) => {
  const [druidWebChatState, druidWebChatDispatch, ,] = useContext<DruidWebchatContextType>(DruidWebchatContext);
  const druidLocalize = useDruidLocalize();
  //
  return [...singleToArray(projectedMergedChatOptions.cardActionMiddleware), ({ dispatch }) => next => (args) => {
    const { cardAction, /*getSignInUrl, target*/ } = args;
    const bfState = druidWebChatState.store.getState();
    switch(cardAction.type) {
      case 'imBack': {
        if(cardAction.value === '_druidInternal_suggestedAction_cancel_button') {
          dispatch({
            type: 'WEB_CHAT/SEND_MESSAGE',
            payload: {
              text: druidLocalize("DRUID_CANCEL_ACTION_TEXT"),
              _druidInternal_suggestedAction_cancel_button: true,
              channelData: { buttonValue: 'UserCanceledInput', buttonType: cardAction.type }
            }
          });
  
          druidWebChatDispatch({
            type: 'INTERNAL/DISABLE_UNTIL_ANY_CONFIRMED_ACTIVITY'
          });
  
          return false;
        } else {
          druidWebChatDispatch({
            type: 'INTERNAL/DISABLE_CARDACTIONS_UNTIL_ANY_CONFIRMED_ACTIVITY',
            payload: { fromBot: true }
          });
        }
        
        unstable_batchedUpdates(async () => {
          if (bfState?.suggestedActions?.length && druidWebChatState.pristineMergedChatOptions?.styleOptions
            && druidWebChatState.pristineMergedChatOptions?.styleOptions?.suggestedActionLayout !== druidWebChatState.mergedChatOptions.styleOptions?.suggestedActionLayout) {
            druidWebChatDispatch({
              type: 'SET_MERGEDCHATOPTIONS',
              payload: {
                styleOptions: {
                  suggestedActionLayout: druidWebChatState.pristineMergedChatOptions?.styleOptions?.suggestedActionLayout
                }
              }
            });
          }
          dispatch({
            type: 'WEB_CHAT/SEND_MESSAGE',
            payload: {
              text: `${cardAction.title || cardAction.text}`,
              channelData: { buttonValue: `${cardAction.value || cardAction.title}`, buttonType: cardAction.type }
            }
          });

          if(druidWebChatState.mergedChatOptions?.UI?.accessibilityFeaturesEnabled) {
            if(druidWebChatState.inputBlocked) {
              druidWebChatDispatch({ type: 'SET_INPUT_BLOCKED', payload: false });
            }
            setTimeout(() => { 
              if(document.activeElement?.nodeName?.toLowerCase() === 'body') {
                DruidWebchat_v2?.hooks?.focus("sendBoxWithoutKeyboard");
              }
              // if setTimeout is set to 0, sendbox remains readonly. component/SendBox/TextBox.tsx -> focusCallback() issue.
            }, 10);
          }
        });
        break;
      }

      case 'openUrl': {
        // if the action is triggered by Hero/Thumbnail with image and tap attribute -> there is no title -> no message is sent
        cardAction.title && dispatch({
          type: 'WEB_CHAT/SEND_MESSAGE',
          payload: {
            text: cardAction.title,
            channelData: { buttonValue: cardAction.value, buttonType: cardAction.type }
          }
        });
        if(!cardAction.value) { // if value is null (flowstep with type H or T, without navigationUrl defined -> tap empty object -> no cardAction.value -> then skip  )
          return null;
        }
        return next(args);
      }

      default : {
        return next(args);
      }
    }
  }].filter((item) => !!item) as OneOrMany<CardActionMiddleware>;
}

export const useAvatarMiddleware = (projectedMergedChatOptions: WebChatOptions) => {
  const [,, getState ,] = useContext<DruidWebchatContextType>(DruidWebchatContext);
  
  return [...singleToArray(projectedMergedChatOptions.avatarMiddleware), () => next => ({ fromUser, activity, ...otherArgs }) => {
    if(!!activity?.channelData?.routedMessageId && getState()?.mergedChatOptions?.UI?.avatarLivechatImage) {
      const renderAvatar = next({ fromUser, activity, ...otherArgs });
      return renderAvatar &&
         (() => (
          <AgentAvatar activity={activity}></AgentAvatar>
         ))
     
    } else {
      return next({ fromUser, activity, ...otherArgs })
    }
   }].filter((item) => !!item) as OneOrMany<AvatarMiddleware>;
} 
const AgentAvatar = ({ activity }: { activity: DirectLineActivity}) => {
  const [,, getState] = useContext<DruidWebchatContextType>(DruidWebchatContext);
  const avatarLivechatImage = getState()?.mergedChatOptions?.UI?.avatarLivechatImage

  const styleToEmotionObject = useStyleToEmotionObject();
  const [{ avatar: avatarStyleSet, imageAvatar: imageAvatarStyleSet }] = useStyleSet();
  const localize = useLocalizer();
  const druidLocalize = useDruidLocalize(localize);

  const ROOT_STYLE = {
    overflow: 'hidden',
    position: 'relative',
  
    '> *': {
      left: 0,
      position: 'absolute',
      top: 0
    }
  };
 
  const rootClassName = styleToEmotionObject(ROOT_STYLE) + '';
  return ((
        <div
        className={classNames(
          'webchat__defaultAvatar',
          rootClassName,
          avatarStyleSet + '',
        )}
      >
        <div className={classNames('webchat__imageAvatar', imageAvatarStyleSet + '')}>
            <CroppedImage
              alt=""
              className="webchat__imageAvatar__image"
              height="100%"
              src={avatarLivechatImage}
              width="100%"
            />
          </div>
      </div>
      ))

}

export const useAtttachmentMiddleware = (druidImagesViewerRef: React.RefObject<DruidImageViewHandle>, projectedMergedChatOptions: WebChatOptions) => {
  const [druidWebChatState, , getState ,] = useContext<DruidWebchatContextType>(DruidWebchatContext);
  return [...singleToArray(projectedMergedChatOptions.attachmentMiddleware), () => next => (args) => {
    const { activity, attachment, ...others } = args;
    const { activities }: { activities: any[] } = druidWebChatState.store.getState();
    const { cardActionsDisabled } = getState();
    // const messageActivities = activities.filter(activity => activity.type === 'message');
    // const messageWithAttachmentActivities = messageActivities.filter(activity => ((activity.attachments && activity.attachments.length) || activity.suggestedActions) && activityFromBot(activity));

    const allNonIgnorableActivities = activities.filter(activity => !ignorableActivity(activity));
    const recentNonIgnorableActivity = allNonIgnorableActivities?.pop()?.id === activity?.id;
    const { isRepliedToActivity = false } = {} = activity.channelData || {}
    // const recentBotMessage = messageActivities.pop()?.id === activity?.id; // could change to recentUserMessage
    // const recentBotMessageWithAttachments = messageWithAttachmentActivities.pop()?.id === activity?.id; // could change to recentUserMessage

    const isActivityFromUser = !activityFromBot(activity);
    // const hasChannelData_RenderHtml = true || activity?.channelData?.renderHtml === true;

    // if(hasChannelData_RenderHtml && activity.text) {
    //   var doc = new DOMParser().parseFromString(activity.text, "text/html");
    //   attachment.content = doc.documentElement.textContent;
    // }
    

    if (/^image\//u.test(attachment.contentType)) {
      return <ImageAttachmentWrapper attachment={attachment} viewPictureHandler={(idx) => {
        if (!!~idx) {
          unstable_batchedUpdates(() => {
            druidImagesViewerRef.current?.setShow(true);
            druidImagesViewerRef.current?.setCurrentPictureIndex(idx);
          });

        }

      }} />
    }
    switch (attachment.contentType) {
      case 'application/vnd.microsoft.card.hero': {

        if(attachment.content?.images?.length) {
          attachment.content.images = removeTapFromImagesAttachmentIfEmpty(attachment.content.images);
        }
        return <HeroCardWrapper
          attachment={attachment}
          // disabled={false}
          disabled={/*!recentBotMessageWithAttachments || */(!recentNonIgnorableActivity && activity?.channelData?.inactivateCardAfterUsage === true) || cardActionsDisabled || isRepliedToActivity}
        >
        </HeroCardWrapper>;
      }

      case 'application/vnd.microsoft.card.thumbnail': {
        if(attachment.content?.images?.length) {
          attachment.content.images = removeTapFromImagesAttachmentIfEmpty(attachment.content.images);
        }
        return <ThumbnailCardWrapper
          attachment={attachment}
          disabled={/*!recentBotMessageWithAttachments || */ (!recentNonIgnorableActivity && activity?.channelData?.inactivateCardAfterUsage === true) || cardActionsDisabled || isRepliedToActivity}
        >
        </ThumbnailCardWrapper>;
      }

      case 'application/vnd.microsoft.card.adaptive': {
        const hiddenAdaptiveCardId = activity?.channelData?.hiddenAdaptiveCardId;
        const valuesActivity = activities?.filter(act => act.type === 'message' && act.value?.hiddenAdaptiveCardId === hiddenAdaptiveCardId).pop();
        // disable feature when accessibilityFeaturesEnabled == true => the focus is lost and body is focused.
        if(!druidWebChatState?.mergedChatOptions?.UI?.accessibilityFeaturesEnabled && valuesActivity?.value && (!activity.channelData?.adaptiveCard_valuesRestored_fromActivityId || activity.channelData?.adaptiveCard_valuesRestored_fromActivityId !== valuesActivity.id)) {
          Object.entries<any>(valuesActivity.value).forEach(([key, value]) => {
            const field = findNestedObjectByKey(attachment.content?.body, "id", key); //?.find(field => field.id === key);
            field && (field.value = field.type !== "Input.Number" ? value : parseInt(value?.toString()) || 0);
          });
          attachment.content = {...attachment.content };
          activity.channelData ??= {};
          activity.channelData.adaptiveCard_valuesRestored_fromActivityId = valuesActivity.id;// valuesActivity.id;
        }
        return (
          <AdaptiveCardContent
            actionPerformedClassName="card__action--performed"
            content={attachment.content}
            disabled={/*!recentBotMessageWithAttachments || */( (!recentNonIgnorableActivity || valuesActivity) && activity?.channelData?.inactivateCardAfterUsage === true) || cardActionsDisabled || isRepliedToActivity}
          />
        );
      }
        

      case 'text/plain': {
       
        // if(hasChannelData_RenderHtml || !isActivityFromUser) {
        //   attachment.contentType = "text/markdown";
        //   return next(args);
        // }

        const hasUrl = attachment.content?.split(/(?:,|;|-| |"|<|>)+/).find(subString => isUrl(subString, { require_protocol: false, require_valid_protocol: false }));
        const hasHTMLTag = !!~attachment.content?.indexOf('>') || !!~attachment.content?.indexOf('<');

        if (hasUrl && (!isActivityFromUser || !hasHTMLTag)) {
          attachment.contentType = "text/markdown";
        }

        return next(args);
      }

      default:
        return next(args);

    }
  }].filter((item) => !!item) as OneOrMany<AttachmentMiddleware>;
}

export const useActivityMiddleware = (projectedMergedChatOptions: WebChatOptions) => {
  const [druidWebChatState, druidWebChatDispatch , getDruidState ,] = useContext<DruidWebchatContextType>(DruidWebchatContext);
  const druidLocalize = useDruidLocalize();
  const { styleOptions: { paddingRegular } = {}
  } = useMergedOptions();
  // projectedMergedChatOptions.activityMiddleware,
  return [...singleToArray(projectedMergedChatOptions.activityMiddleware), () => next => (...setupArgs) => {
    const currentBFStoreState = druidWebChatState.store.getState();
    const messageActivities = currentBFStoreState.activities.filter(act => act.type === 'message').reverse();
    const { activity = {} } = setupArgs[0];
    // paddingRegular
    if(activity.channelData?.conversationHistory) {
      const currentDruidState = getDruidState();
      const { isShown, loadAllMessages, separator } = activity.channelData?.conversationHistory;
      if(loadAllMessages) {
        if(currentDruidState.conversationHistoryShowAll) {
          return false;
        } else {
          return <ConversationHistorySeparator onClick={() => druidWebChatDispatch({type: 'CONVERSATION_HISTORY_SHOWALL', payload: true})}>Load all messages &uarr;</ConversationHistorySeparator>;
        }
      }
      if(separator) {
        return <ConversationHistorySeparator>Active conversation</ConversationHistorySeparator>;
      }
  
      if(isShown === false && !currentDruidState.conversationHistoryShowAll) {
        return false;
      }
    }
  
    if (activity.type === "message") {
      if (!!activity.value) { // submit action on custom adaptivecard -> should not render anything
        return false;
      }

      if ((messageActivities && messageActivities.indexOf(activity) > 200)) {
        return <div className="webchat__basic-transcript__activity-not-rendered"></div>; // fixes other bugs (not scrolling to bottom). could not return false, returned empty div but there are white spaces on top of the transcript.
      }
      if ((activity.attachments || []).length > 1 && activity.attachmentLayout === 'carousel' && activity.text) { //separate the message from attachment to render two "activities". this way the carousel scrolls only the attachments
        return (renderAttachment, props) => {
          const { attachments, attachmentLayout, ...messageWithoutAttachments } = activity;
          const { text, ...messageWithoutText } = activity;
          return (
            <DruidActivityWrapper activity={activity}>
              <div style={{ paddingBottom: paddingRegular }}>
                <StackedLayout activity={messageWithoutAttachments} renderAttachment={renderAttachment}  {...{ ...props, ...{ renderActivityStatus: (_: { hideTimestamp : boolean } ) => false } }} />
              </div>
              <CarouselLayout activity={messageWithoutText} renderAttachment={renderAttachment} {...props} renderAvatar={false} />
            </DruidActivityWrapper>
          )
        };
      }

      // MM: careful -> two cases are handled here
      if(shouldEnter_sensitiveDataInputMode(activity)) { //|| shouldEnter_fileUploadInputMode(activity)
        activity.suggestedActions = {
          actions: [
            { type: "imBack", title: druidLocalize("DRUID_CANCEL_BUTTON_TEXT"), text: druidLocalize("DRUID_CANCEL_ACTION_TEXT"), value: "_druidInternal_suggestedAction_cancel_button", channelData: { buttonClass: "" }, _druidInternal_suggestedAction_cancel_button: true  }
          ]
        }
      }

      return (...renderArgs) => <DruidActivityWrapper activity={activity}> <React.Fragment>{ next(setupArgs[0])(...renderArgs) }</React.Fragment> </DruidActivityWrapper>;

    }
    return next(...setupArgs);
  }
  ].filter((item) => !!item) as OneOrMany<ActivityMiddleware>;
}

export const useScrollToEndButtonMiddleware = (projectedMergedChatOptions: WebChatOptions, debounceHideSuggestedActions: BehaviorSubject<boolean>) => {
  return useMemo(() => {
    var ignoreHideSuggestedActions: boolean = false;
    var hideSuggestedActionsTimeout: any = null;
    return [...singleToArray(projectedMergedChatOptions.scrollToEndButtonMiddleware), () => (next) => (args) => {
      if(ignoreHideSuggestedActions) {
        return next(args);
      }
      //atEnd could be used for suggestedAction hiding
      var { atEnd, styleOptions: { scrollToEndButtonBehavior }, unread } = args;
      // debounceHideSuggestedActions.next(!atEnd);
      // var atEndRefined = atEnd;
      // if (!atEnd) {
      //   const scrollableTranscript = document.querySelector<HTMLDivElement>('.webchat__basic-transcript__scrollable');
      //   if (scrollableTranscript) {
      //     const { scrollHeight, scrollTop, offsetHeight } = scrollableTranscript;
      //     atEnd = scrollHeight - scrollTop - offsetHeight < 10;
      //     // if (atEndRefined !== isAtEndRefined) return next(args);
      //   }
      // }
      // args.atEnd = atEnd;

      // if(hideSuggestedActionsTimeout) {
      //   clearTimeout(hideSuggestedActionsTimeout);
      // }
      // if (!atEndRefined) {
       
      //   hideSuggestedActionsTimeout = setTimeout(() => debounceHideSuggestedActions.next(!atEndRefined), 500);

      // } else {
        const hideSuggestedActions = !atEnd;
        if(debounceHideSuggestedActions.getValue() !== hideSuggestedActions && !hideSuggestedActions) {
          ignoreHideSuggestedActions = true;
          if(hideSuggestedActionsTimeout) {
            clearTimeout(hideSuggestedActionsTimeout);
          }
          hideSuggestedActionsTimeout = setTimeout(() => ignoreHideSuggestedActions = false, 500);
        }
        debounceHideSuggestedActions.next(!atEnd);
      // }

      return next(args);
    }].filter((item) => !!item) as OneOrMany<ScrollToEndButtonMiddleware>
  }, [projectedMergedChatOptions.scrollToEndButtonMiddleware, debounceHideSuggestedActions]);
}